import { levelFor, type ProjectPlacement } from '../../app/session'

/**
 * Today, as a calendar date on the project's own clock.
 *
 * A day in the log is a date on the site, not in the browser: a project
 * manager reading from another time zone at 9 p.m. would otherwise open
 * tomorrow's log for a crew that has not started it. `en-CA` is used only
 * because it formats as `YYYY-MM-DD`, which is what the API takes.
 */
export function projectToday(timeZone: string | null | undefined, now: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZone || undefined,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now)
  } catch {
    // An unknown zone name is the project's data to fix, not a reason to
    // refuse to open the day; the browser's own date is the nearest answer.
    return new Intl.DateTimeFormat('en-CA').format(now)
  }
}

/**
 * A log date for reading.
 *
 * Formatted in UTC on purpose. `new Date('2026-09-03')` is midnight UTC, and
 * rendering that in a browser west of Greenwich prints 2 September — the day
 * before the one the crew recorded.
 */
export function formatLogDate(value: string): string {
  const parsed = new Date(`${value}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

/**
 * Whether to offer the daily log's writes on this project.
 *
 * What is offered, never what is allowed: the evaluator on the instance
 * decides, and a refusal it sends is shown. This only keeps a reader from
 * being handed a form whose every submission comes back 403.
 */
export function mayWrite(placement: ProjectPlacement): boolean {
  const level = levelFor(placement, 'dailylog')
  return level === 'standard' || level === 'admin'
}
